import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  ListItemButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ExitToApp as DischargeIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  MedicalServices as MedicalIcon,
  CurrencyRupee as MoneyIcon,
  Hotel as BedIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as ReceiptIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { collection, query, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { format } from 'date-fns';
import RoomManagementService from '../services/RoomManagementService';

// Types
interface Patient {
  id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
  email?: string;
  address?: string;
  patientType: 'IP' | 'OP';
  department?: string;
  doctor: string;
  bloodGroup?: string;
  room?: string;
  bedNo?: string;
  admissionDate?: string;
  registrationDate?: string;
  statusColor: string;
  statusText: string;
  emergencyContact?: string;
}

interface Appointment {
  id: string;
  patientId: string;
  doctorName: string;
  serviceName: string;
  date: string;
  time: string;
  status: string;
  amount?: number;
}

interface Payment {
  id: string;
  patientId: string;
  amount: number;
  type: string;
  paymentMethod: string;
  date: string;
  status: string;
  description?: string;
}

interface Medication {
  name: string;
  dosage: string;
  frequency: string;
  duration: string;
}

interface DischargeRecord {
  id: string;
  patientId: string;
  patientName: string;
  patientPhone: string;
  patientType: 'IP' | 'OP';
  admissionDate: string;
  dischargeDate: string;
  totalDays: number;
  room?: string;
  bedNo?: string;
  department?: string;
  primaryDoctor: string;
  allDoctors: string[];
  totalAmountSpent: number;
  diagnosis: string;
  treatment: string;
  medications: Medication[];
  followUpInstructions: string;
  condition: string;
  restrictions?: string;
  emergencyContact?: string;
  appointmentsCount: number;
  paymentsCount: number;
  createdAt: string;
  createdBy: string;
}

const Discharge: React.FC = () => {
  // State
  const [patients, setPatients] = useState<Patient[]>([]);
  const [dischargeRecords, setDischargeRecords] = useState<DischargeRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<DischargeRecord[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPatientSelectionModal, setShowPatientSelectionModal] = useState(false);
  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });

  // Discharge form state
  const [dischargeForm, setDischargeForm] = useState({
    diagnosis: '',
    treatment: '',
    condition: 'Stable',
    followUpInstructions: '',
    restrictions: '',
    medications: [] as Medication[]
  });

  const [newMedication, setNewMedication] = useState({
    name: '',
    dosage: '',
    frequency: '',
    duration: ''
  });

  // Load data from Firebase with real-time updates
  useEffect(() => {
    const unsubscribers: (() => void)[] = [];

    // Load all patients (both IP and OP) - filter to show only active patients (not discharged)
    const patientsQuery = query(collection(db, 'patients'));
    const unsubPatients = onSnapshot(patientsQuery, (snapshot) => {
      const patientsList: Patient[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        // Filter to show only active patients (not discharged) for patient selection
        if (data.statusText !== 'Discharged') {
          patientsList.push({
            id: doc.id,
            name: data.name || '',
            age: data.age || 0,
            gender: data.gender || '',
            phone: data.phone || '',
            email: data.email,
            address: data.address,
            patientType: data.patientType || 'OP',
            department: data.department,
            doctor: data.doctor || '',
            bloodGroup: data.bloodGroup,
            room: data.room,
            bedNo: data.bedNo,
            admissionDate: data.admissionDate,
            registrationDate: data.registrationDate,
            statusColor: data.statusColor || '#10B981',
            statusText: data.statusText || 'Active',
            emergencyContact: data.emergencyContact
          });
        }
      });
      // Sort by admission/registration date client-side
      patientsList.sort((a, b) => {
        const dateA = a.admissionDate || a.registrationDate || '';
        const dateB = b.admissionDate || b.registrationDate || '';
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.localeCompare(dateA);
      });
      setPatients(patientsList);
      console.log('✅ Loaded active patients (IP & OP):', patientsList.length);
    });
    unsubscribers.push(unsubPatients);

    // Load discharge records
    const dischargeQuery = query(collection(db, 'discharges'), orderBy('createdAt', 'desc'));
    const unsubDischarges = onSnapshot(dischargeQuery, (snapshot) => {
      const records: DischargeRecord[] = [];
      snapshot.forEach((doc) => {
        records.push({ id: doc.id, ...doc.data() } as DischargeRecord);
      });
      setDischargeRecords(records);
      setFilteredRecords(records);
      console.log('✅ Loaded discharge records:', records.length);
    });
    unsubscribers.push(unsubDischarges);

    // Load appointments
    const appointmentsQuery = query(collection(db, 'appointments'));
    const unsubAppointments = onSnapshot(appointmentsQuery, (snapshot) => {
      const apptList: Appointment[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        apptList.push({
          id: doc.id,
          patientId: data.patientId || '',
          doctorName: data.doctorName || '',
          serviceName: data.serviceName || '',
          date: data.date || '',
          time: data.time || '',
          status: data.status || '',
          amount: data.amount
        });
      });
      setAppointments(apptList);
      console.log('✅ Loaded appointments:', apptList.length);
    });
    unsubscribers.push(unsubAppointments);

    // Load payments
    const paymentsQuery = query(collection(db, 'payments'));
    const unsubPayments = onSnapshot(paymentsQuery, (snapshot) => {
      const payList: Payment[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        payList.push({
          id: doc.id,
          patientId: data.patientId || '',
          amount: data.amount || 0,
          type: data.type || '',
          paymentMethod: data.paymentMethod || '',
          date: data.date || '',
          status: data.status || '',
          description: data.description
        });
      });
      setPayments(payList);
      console.log('✅ Loaded payments:', payList.length);
    });
    unsubscribers.push(unsubPayments);

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, []);

  // Filter records
  useEffect(() => {
    let filtered = dischargeRecords;
    if (searchTerm) {
      filtered = filtered.filter(record =>
        record.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.patientId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.primaryDoctor.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredRecords(filtered);
  }, [searchTerm, dischargeRecords]);

  // Functions
  const showNotification = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSelectPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowPatientSelectionModal(false);
    setShowDischargeModal(true);
    
    // Reset form
    setDischargeForm({
      diagnosis: '',
      treatment: '',
      condition: 'Stable',
      followUpInstructions: '',
      restrictions: '',
      medications: []
    });
  };

  const handleAddMedication = () => {
    if (newMedication.name && newMedication.dosage) {
      setDischargeForm({
        ...dischargeForm,
        medications: [...dischargeForm.medications, { ...newMedication }]
      });
      setNewMedication({ name: '', dosage: '', frequency: '', duration: '' });
    }
  };

  const handleRemoveMedication = (index: number) => {
    const updatedMedications = dischargeForm.medications.filter((_, i) => i !== index);
    setDischargeForm({ ...dischargeForm, medications: updatedMedications });
  };

  const calculatePatientStats = (patientId: string) => {
    const patientAppointments = appointments.filter(a => a.patientId === patientId);
    const patientPayments = payments.filter(p => p.patientId === patientId);
    const totalSpent = patientPayments.reduce((sum, p) => sum + p.amount, 0);
    const uniqueDoctors = Array.from(new Set(patientAppointments.map(a => a.doctorName)));
    
    return {
      appointments: patientAppointments,
      payments: patientPayments,
      totalSpent,
      uniqueDoctors,
      appointmentsCount: patientAppointments.length,
      paymentsCount: patientPayments.length
    };
  };

  const handleDischargePatient = async () => {
    if (!selectedPatient) return;

    if (!dischargeForm.diagnosis || !dischargeForm.treatment) {
      showNotification('Please fill in diagnosis and treatment details', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const stats = calculatePatientStats(selectedPatient.id);
      const admissionDate = selectedPatient.admissionDate || selectedPatient.registrationDate || new Date().toISOString();
      const dischargeDate = new Date().toISOString();
      const daysDiff = Math.ceil((new Date(dischargeDate).getTime() - new Date(admissionDate).getTime()) / (1000 * 60 * 60 * 24));

      // Create discharge record
      const dischargeData: Omit<DischargeRecord, 'id'> = {
        patientId: selectedPatient.id,
        patientName: selectedPatient.name,
        patientPhone: selectedPatient.phone,
        patientType: selectedPatient.patientType,
        admissionDate: admissionDate,
        dischargeDate: dischargeDate,
        totalDays: daysDiff > 0 ? daysDiff : 1,
        room: selectedPatient.room,
        bedNo: selectedPatient.bedNo,
        department: selectedPatient.department,
        primaryDoctor: selectedPatient.doctor,
        allDoctors: stats.uniqueDoctors,
        totalAmountSpent: stats.totalSpent,
        diagnosis: dischargeForm.diagnosis,
        treatment: dischargeForm.treatment,
        medications: dischargeForm.medications,
        followUpInstructions: dischargeForm.followUpInstructions,
        condition: dischargeForm.condition,
        restrictions: dischargeForm.restrictions,
        emergencyContact: selectedPatient.emergencyContact,
        appointmentsCount: stats.appointmentsCount,
        paymentsCount: stats.paymentsCount,
        createdAt: dischargeDate,
        createdBy: 'Admin' // Replace with actual user
      };

      // Add to discharges collection
      await addDoc(collection(db, 'discharges'), dischargeData);

      // Update patient status
      const patientRef = doc(db, 'patients', selectedPatient.id);
      await updateDoc(patientRef, {
        statusText: 'Discharged',
        statusColor: '#6B7280',
        dischargeDate: dischargeDate,
        updatedAt: dischargeDate
      });

      // Release bed if assigned - using the new utility function
      if (selectedPatient.room && selectedPatient.bedNo) {
        console.log(`🛏️ Releasing bed ${selectedPatient.bedNo} in room ${selectedPatient.room}...`);
        
        const result = await RoomManagementService.updateRoomAvailabilityForPatient(
          selectedPatient.id,
          selectedPatient.room,
          selectedPatient.bedNo
        );
        
        if (result.success) {
          console.log(`✅ ${result.message}`);
        } else {
          console.warn(`⚠️ ${result.message}`);
        }
      }

      showNotification(`Patient ${selectedPatient.name} discharged successfully!`, 'success');
      setShowDischargeModal(false);
      setSelectedPatient(null);
      setDischargeForm({
        diagnosis: '',
        treatment: '',
        condition: 'Stable',
        followUpInstructions: '',
        restrictions: '',
        medications: []
      });
    } catch (error: any) {
      console.error('❌ Error discharging patient:', error);
      showNotification(`Failed to discharge patient: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewRecord = (record: DischargeRecord) => {
    // Create a detailed discharge summary view in a new window (same as invoice functionality)
    const summaryWindow = window.open('', '_blank', 'width=800,height=600');
    if (summaryWindow) {
      const patientPayments = payments.filter(p => p.patientId === record.patientId);
      const totalPaid = patientPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      
      const summaryHTML = generateDischargeSummaryHTML(record, patientPayments, totalPaid);
      
      summaryWindow.document.write(summaryHTML);
      summaryWindow.document.close();
    }
  };

  const handlePrintRecord = (record: DischargeRecord) => {
    // Open the discharge summary and trigger print
    const summaryWindow = window.open('', '_blank', 'width=800,height=600');
    if (summaryWindow) {
      const patientPayments = payments.filter(p => p.patientId === record.patientId);
      const totalPaid = patientPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      
      // Generate the same HTML as handleViewRecord
      const summaryHTML = generateDischargeSummaryHTML(record, patientPayments, totalPaid);
      
      summaryWindow.document.write(summaryHTML);
      summaryWindow.document.close();
      
      // Trigger print after content loads
      summaryWindow.onload = () => {
        summaryWindow.print();
      };
    }
  };

  const handleDownloadRecord = (record: DischargeRecord) => {
    // Create HTML content and download as file
    const patientPayments = payments.filter(p => p.patientId === record.patientId);
    const totalPaid = patientPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    
    const summaryHTML = generateDischargeSummaryHTML(record, patientPayments, totalPaid);
    
    // Create a Blob from the HTML content
    const blob = new Blob([summaryHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `Discharge_Summary_${record.patientName.replace(/\s+/g, '_')}_${record.id}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    showNotification('Discharge summary downloaded successfully', 'success');
  };

  const generateDischargeSummaryHTML = (record: DischargeRecord, patientPayments: Payment[], totalPaid: number): string => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Discharge Summary - ${record.patientName}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px;
            max-width: 900px;
            margin: 0 auto;
          }
          .header { 
            text-align: center; 
            border-bottom: 2px solid #333; 
            padding-bottom: 10px; 
            margin-bottom: 20px; 
          }
          .section { 
            margin: 25px 0;
            padding: 15px;
            border: 1px solid #ddd;
            background-color: #fafafa;
          }
          .section h3 {
            margin: 0 0 15px 0;
            padding-bottom: 8px;
            border-bottom: 1px solid #ddd;
            font-size: 16px;
          }
          .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px 20px;
            margin: 10px 0;
          }
          .info-row { 
            display: flex;
            padding: 5px 0;
          }
          .info-row strong {
            min-width: 180px;
            display: inline-block;
          }
          .info-text {
            margin: 10px 0;
            padding: 10px;
            background-color: white;
            border-left: 3px solid #333;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 15px 0;
            background-color: white;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 10px; 
            text-align: left; 
          }
          th { 
            background-color: #f2f2f2;
            font-weight: bold;
          }
          .total-row {
            font-weight: bold;
            background-color: #f2f2f2;
          }
          .summary-box {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            margin: 15px 0;
          }
          .summary-item {
            padding: 12px;
            background-color: white;
            border: 1px solid #ddd;
            text-align: center;
          }
          .summary-label {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }
          .summary-value {
            font-size: 20px;
            font-weight: bold;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            border-top: 1px solid #ddd;
            padding-top: 15px;
            color: #666;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>KBR LIFE CARE</h1>
          <h2>Discharge Summary</h2>
        </div>

        <!-- Patient Information -->
        <div class="section">
          <h3>Patient Information</h3>
          <div class="info-grid">
            <div class="info-row"><strong>Patient Name:</strong> <span>${record.patientName}</span></div>
            <div class="info-row"><strong>Patient ID:</strong> <span>${record.patientId}</span></div>
            <div class="info-row"><strong>Patient Type:</strong> <span>${record.patientType}</span></div>
            <div class="info-row"><strong>Phone:</strong> <span>${record.patientPhone}</span></div>
            ${record.room ? `<div class="info-row"><strong>Room:</strong> <span>${record.room}</span></div>` : ''}
            ${record.bedNo ? `<div class="info-row"><strong>Bed No:</strong> <span>${record.bedNo}</span></div>` : ''}
            ${record.department ? `<div class="info-row"><strong>Department:</strong> <span>${record.department}</span></div>` : ''}
            ${record.emergencyContact ? `<div class="info-row"><strong>Emergency Contact:</strong> <span>${record.emergencyContact}</span></div>` : ''}
          </div>
        </div>

        <!-- Admission Details -->
        <div class="section">
          <h3>Admission Details</h3>
          <div class="info-grid">
            <div class="info-row"><strong>Admission Date:</strong> <span>${formatDate(record.admissionDate)}</span></div>
            <div class="info-row"><strong>Discharge Date:</strong> <span>${formatDate(record.dischargeDate)}</span></div>
            <div class="info-row"><strong>Total Days:</strong> <span>${record.totalDays} days</span></div>
            <div class="info-row"><strong>Condition at Discharge:</strong> <span>${record.condition}</span></div>
          </div>
        </div>

        <!-- Medical Team -->
        <div class="section">
          <h3>Medical Team</h3>
          <div class="info-row"><strong>Primary Doctor:</strong> <span>${record.primaryDoctor}</span></div>
          <div class="info-row"><strong>All Attending Doctors:</strong> <span>${record.allDoctors.join(', ')}</span></div>
        </div>

        <!-- Medical Details -->
        <div class="section">
          <h3>Medical Details</h3>
          <div class="info-row"><strong>Diagnosis:</strong></div>
          <div class="info-text">${record.diagnosis}</div>
          
          <div class="info-row" style="margin-top: 15px;"><strong>Treatment:</strong></div>
          <div class="info-text">${record.treatment}</div>
          
          <div class="info-row" style="margin-top: 15px;"><strong>Follow-up Instructions:</strong></div>
          <div class="info-text">${record.followUpInstructions}</div>
          
          ${record.restrictions ? `
          <div class="info-row" style="margin-top: 15px;"><strong>Restrictions:</strong></div>
          <div class="info-text">${record.restrictions}</div>
          ` : ''}
        </div>

        <!-- Medications -->
        ${record.medications.length > 0 ? `
        <div class="section">
          <h3>Prescribed Medications</h3>
          <table>
            <thead>
              <tr>
                <th>Medicine Name</th>
                <th>Dosage</th>
                <th>Frequency</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              ${record.medications.map(med => `
                <tr>
                  <td>${med.name}</td>
                  <td>${med.dosage}</td>
                  <td>${med.frequency}</td>
                  <td>${med.duration}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}

        <!-- Financial Summary -->
        <div class="section">
          <h3>Financial Summary</h3>
          <div class="summary-box">
            <div class="summary-item">
              <div class="summary-label">Total Amount Spent</div>
              <div class="summary-value">₹${record.totalAmountSpent.toLocaleString('en-IN')}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Total Payments</div>
              <div class="summary-value">${patientPayments.length}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Balance Due</div>
              <div class="summary-value">₹${(record.totalAmountSpent - totalPaid).toLocaleString('en-IN')}</div>
            </div>
          </div>
          
          ${patientPayments.length > 0 ? `
          <h4 style="margin: 20px 0 10px 0; font-size: 14px;">Payment History</h4>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Method</th>
                <th>Type</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${patientPayments
                .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                .map(payment => `
                <tr>
                  <td>${formatDate(payment.date)}</td>
                  <td>${payment.paymentMethod || 'N/A'}</td>
                  <td>${payment.type || 'General'}</td>
                  <td>${payment.description || '-'}</td>
                  <td style="font-weight: bold;">₹${payment.amount.toLocaleString('en-IN')}</td>
                  <td>${payment.status || 'Completed'}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="4">Total Paid</td>
                <td>₹${totalPaid.toLocaleString('en-IN')}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
          ` : `<p style="text-align: center; padding: 15px; color: #666;">No payment records found for this patient.</p>`}
        </div>

        <!-- Utilization Summary -->
        <div class="section">
          <h3>Utilization Summary</h3>
          <div class="info-grid">
            <div class="info-row"><strong>Total Appointments:</strong> <span>${record.appointmentsCount}</span></div>
            <div class="info-row"><strong>Total Payments Made:</strong> <span>${record.paymentsCount}</span></div>
          </div>
        </div>

        <div class="footer">
          <p>Discharge Summary ID: ${record.id}</p>
          <p>Generated on: ${formatDate(record.createdAt)} | Generated by: KBR LIFE CARE</p>
          <div style="text-align: right; margin-top: 60px; padding-right: 20px;">
            <p style="margin: 0; font-weight: bold;">Authorized Signature</p>
            <div style="border-bottom: 1px solid #333; width: 200px; margin: 50px 0 5px auto;"></div>
            <p style="margin: 0; font-size: 11px;">KBR LIFE CARE</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleDeleteRecord = async (recordId: string) => {
    if (!window.confirm('Are you sure you want to delete this discharge record?')) return;

    try {
      await deleteDoc(doc(db, 'discharges', recordId));
      showNotification('Discharge record deleted successfully', 'success');
    } catch (error: any) {
      console.error('❌ Error deleting record:', error);
      showNotification(`Failed to delete record: ${error.message}`, 'error');
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Statistics
  const totalDischarges = dischargeRecords.length;
  const thisMonthDischarges = dischargeRecords.filter(record => {
    const recordDate = new Date(record.dischargeDate);
    const now = new Date();
    return recordDate.getMonth() === now.getMonth() && recordDate.getFullYear() === now.getFullYear();
  }).length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1A202C', mb: 1 }}>
          Discharge Management
        </Typography>
        <Typography variant="body1" sx={{ color: '#718096' }}>
          Manage patient discharge summaries and records
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <Card sx={{ borderLeft: '4px solid #4A90E2' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1A202C' }}>
                  {totalDischarges}
                </Typography>
                <Typography variant="body1" sx={{ color: '#718096' }}>
                  Total Discharges
                </Typography>
              </Box>
              <DischargeIcon sx={{ fontSize: 50, color: '#4A90E2', opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderLeft: '4px solid #10B981' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1A202C' }}>
                  {thisMonthDischarges}
                </Typography>
                <Typography variant="body1" sx={{ color: '#718096' }}>
                  This Month
                </Typography>
              </Box>
              <CalendarIcon sx={{ fontSize: 50, color: '#10B981', opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderLeft: '4px solid #F59E0B' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1A202C' }}>
                  {patients.filter(p => p.statusText !== 'Discharged').length}
                </Typography>
                <Typography variant="body1" sx={{ color: '#718096' }}>
                  Active Patients
                </Typography>
              </Box>
              <PersonIcon sx={{ fontSize: 50, color: '#F59E0B', opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search discharge records by patient name or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Create Discharge Summary Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<AddIcon />}
          onClick={() => setShowPatientSelectionModal(true)}
          sx={{
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            borderRadius: 3,
            backgroundColor: '#4A90E2',
            '&:hover': { backgroundColor: '#357ABD' }
          }}
        >
          Create Discharge Summary
        </Button>
      </Box>

      {/* Discharge Records Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Patient Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Patient ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Admission Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Discharge Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Total Days</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Doctor</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Total Spent</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRecords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No discharge records found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRecords.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>{record.patientName}</TableCell>
                  <TableCell>
                    <Chip label={record.patientId} size="small" sx={{ fontSize: '0.75rem' }} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={record.patientType}
                      size="small"
                      color={record.patientType === 'IP' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(record.admissionDate)}</TableCell>
                  <TableCell>{formatDate(record.dischargeDate)}</TableCell>
                  <TableCell>{record.totalDays} days</TableCell>
                  <TableCell>{record.primaryDoctor}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#10B981' }}>
                    {formatCurrency(record.totalAmountSpent)}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Discharge Summary">
                      <IconButton
                        size="small"
                        onClick={() => handleViewRecord(record)}
                        sx={{ color: '#4A90E2' }}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print Discharge Summary">
                      <IconButton
                        size="small"
                        onClick={() => handlePrintRecord(record)}
                        sx={{ color: '#10B981' }}
                      >
                        <PrintIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download Discharge Summary">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadRecord(record)}
                        sx={{ color: '#F59E0B' }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Record">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRecord(record.id)}
                        sx={{ color: '#EF4444' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Patient Selection Modal */}
      <Dialog
        open={showPatientSelectionModal}
        onClose={() => setShowPatientSelectionModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Select Patient for Discharge
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {patients.filter(p => p.statusText !== 'Discharged').length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                No IP patients available for discharge
              </Typography>
            </Box>
          ) : (
            <List>
              {patients.filter(p => p.statusText !== 'Discharged').map((patient, index) => (
                <React.Fragment key={patient.id}>
                  {index > 0 && <Divider />}
                  <ListItemButton
                    onClick={() => handleSelectPatient(patient)}
                    sx={{
                      py: 2,
                      '&:hover': { backgroundColor: '#F3F4F6' }
                    }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ backgroundColor: '#4A90E2' }}>
                        {patient.name.charAt(0)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {patient.name}
                          </Typography>
                          <Chip label={patient.patientType} size="small" color="primary" />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            Room: {patient.room || 'N/A'} | Bed: {patient.bedNo || 'N/A'} | Doctor: {patient.doctor}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Admitted: {formatDate(patient.admissionDate || '')} | Phone: {patient.phone}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E5E7EB' }}>
          <Button onClick={() => setShowPatientSelectionModal(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Discharge Form Modal */}
      <Dialog
        open={showDischargeModal}
        onClose={() => !isLoading && setShowDischargeModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#4A90E2', color: 'white' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Discharge Summary - {selectedPatient?.name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          {selectedPatient && (
            <Box>
              {/* Patient Full Information */}
              <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Patient Information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                    <Paper sx={{ p: 2, backgroundColor: '#F9FAFB' }}>
                        <Typography variant="subtitle2" color="textSecondary">Personal Details</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2"><strong>Name:</strong> {selectedPatient.name}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Age:</strong> {selectedPatient.age} | <strong>Gender:</strong> {selectedPatient.gender}</Typography>
                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Blood Group:</strong> {selectedPatient.bloodGroup || 'N/A'}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{selectedPatient.phone}</Typography>
                        </Box>
                        {selectedPatient.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <EmailIcon fontSize="small" color="action" />
                            <Typography variant="body2">{selectedPatient.email}</Typography>
                          </Box>
                        )}
                        {selectedPatient.address && (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <LocationIcon fontSize="small" color="action" />
                            <Typography variant="body2">{selectedPatient.address}</Typography>
                          </Box>
                        )}
                      </Paper>

                    <Paper sx={{ p: 2, backgroundColor: '#F9FAFB' }}>
                        <Typography variant="subtitle2" color="textSecondary">Admission Details</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <BedIcon fontSize="small" color="action" />
                          <Typography variant="body2"><strong>Room:</strong> {selectedPatient.room || 'N/A'}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Bed:</strong> {selectedPatient.bedNo || 'N/A'}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CalendarIcon fontSize="small" color="action" />
                          <Typography variant="body2"><strong>Admitted:</strong> {formatDate(selectedPatient.admissionDate || '')}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Department:</strong> {selectedPatient.department || 'N/A'}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MedicalIcon fontSize="small" color="action" />
                          <Typography variant="body2"><strong>Primary Doctor:</strong> {selectedPatient.doctor}</Typography>
                        </Box>
                      </Paper>

                    {/* Financial Summary */}
                    <Paper sx={{ p: 2, backgroundColor: '#FEF3C7' }}>
                        <Typography variant="subtitle2" color="textSecondary">Financial Summary</Typography>
                        <Divider sx={{ my: 1 }} />
                        {(() => {
                          const stats = calculatePatientStats(selectedPatient.id);
                          return (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <MoneyIcon fontSize="small" sx={{ color: '#10B981' }} />
                                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10B981' }}>
                                  {formatCurrency(stats.totalSpent)}
                                </Typography>
                              </Box>
                              <Typography variant="body2"><strong>Total Payments:</strong> {stats.paymentsCount}</Typography>
                              {stats.payments.length > 0 && (
                                <Box sx={{ mt: 1, maxHeight: 100, overflow: 'auto' }}>
                                  {stats.payments.map((payment, idx) => (
                                    <Typography key={idx} variant="caption" display="block">
                                      • {payment.type}: {formatCurrency(payment.amount)} ({formatDate(payment.date)})
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </>
                          );
                        })()}
                      </Paper>

                    {/* Treatment History */}
                    <Paper sx={{ p: 2, backgroundColor: '#DBEAFE' }}>
                        <Typography variant="subtitle2" color="textSecondary">Treatment History</Typography>
                        <Divider sx={{ my: 1 }} />
                        {(() => {
                          const stats = calculatePatientStats(selectedPatient.id);
                          return (
                            <>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Total Appointments:</strong> {stats.appointmentsCount}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Doctors Consulted:</strong> {stats.uniqueDoctors.length}
                              </Typography>
                              {stats.uniqueDoctors.length > 0 && (
                                <Box>
                                  {stats.uniqueDoctors.map((doctor, idx) => (
                                    <Chip key={idx} label={doctor} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                  ))}
                                </Box>
                              )}
                              {stats.appointments.length > 0 && (
                                <Box sx={{ mt: 1, maxHeight: 100, overflow: 'auto' }}>
                                  {stats.appointments.map((appt, idx) => (
                                    <Typography key={idx} variant="caption" display="block">
                                      • {appt.serviceName} - Dr. {appt.doctorName} ({formatDate(appt.date)})
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </>
                          );
                        })()}
                      </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Discharge Form */}
              <Accordion defaultExpanded sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Discharge Summary Form
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Diagnosis"
                        multiline
                        rows={3}
                        value={dischargeForm.diagnosis}
                        onChange={(e) => setDischargeForm({ ...dischargeForm, diagnosis: e.target.value })}
                        required
                      />

                    <TextField
                        fullWidth
                        label="Treatment Provided"
                        multiline
                        rows={3}
                        value={dischargeForm.treatment}
                        onChange={(e) => setDischargeForm({ ...dischargeForm, treatment: e.target.value })}
                        required
                      />

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                      <FormControl fullWidth>
                        <InputLabel>Condition at Discharge</InputLabel>
                        <Select
                          value={dischargeForm.condition}
                          label="Condition at Discharge"
                          onChange={(e) => setDischargeForm({ ...dischargeForm, condition: e.target.value })}
                        >
                          <MenuItem value="Stable">Stable</MenuItem>
                          <MenuItem value="Improved">Improved</MenuItem>
                          <MenuItem value="Recovered">Recovered</MenuItem>
                          <MenuItem value="Critical">Critical</MenuItem>
                          <MenuItem value="Requires Follow-up">Requires Follow-up</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        label="Restrictions/Precautions"
                        value={dischargeForm.restrictions}
                        onChange={(e) => setDischargeForm({ ...dischargeForm, restrictions: e.target.value })}
                      />
                    </Box>

                    <TextField
                        fullWidth
                        label="Follow-up Instructions"
                        multiline
                        rows={2}
                        value={dischargeForm.followUpInstructions}
                        onChange={(e) => setDischargeForm({ ...dischargeForm, followUpInstructions: e.target.value })}
                      />

                    {/* Medications */}
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Discharge Medications
                      </Typography>
                      <Paper sx={{ p: 2, backgroundColor: '#F9FAFB', mb: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: '3fr 3fr 2fr 2fr 2fr' }, gap: 2 }}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Medicine Name"
                            value={newMedication.name}
                            onChange={(e) => setNewMedication({ ...newMedication, name: e.target.value })}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Dosage"
                            value={newMedication.dosage}
                            onChange={(e) => setNewMedication({ ...newMedication, dosage: e.target.value })}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Frequency"
                            value={newMedication.frequency}
                            onChange={(e) => setNewMedication({ ...newMedication, frequency: e.target.value })}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Duration"
                            value={newMedication.duration}
                            onChange={(e) => setNewMedication({ ...newMedication, duration: e.target.value })}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={handleAddMedication}
                            sx={{ height: '40px' }}
                          >
                            Add
                          </Button>
                        </Box>
                      </Paper>

                      {dischargeForm.medications.length > 0 && (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Medicine</strong></TableCell>
                                <TableCell><strong>Dosage</strong></TableCell>
                                <TableCell><strong>Frequency</strong></TableCell>
                                <TableCell><strong>Duration</strong></TableCell>
                                <TableCell><strong>Action</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {dischargeForm.medications.map((med, index) => (
                                <TableRow key={index}>
                                  <TableCell>{med.name}</TableCell>
                                  <TableCell>{med.dosage}</TableCell>
                                  <TableCell>{med.frequency}</TableCell>
                                  <TableCell>{med.duration}</TableCell>
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveMedication(index)}
                                      sx={{ color: '#EF4444' }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
          <Button
            onClick={() => setShowDischargeModal(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <DischargeIcon />}
            onClick={handleDischargePatient}
            disabled={isLoading}
            sx={{
              backgroundColor: '#10B981',
              '&:hover': { backgroundColor: '#059669' }
            }}
          >
            {isLoading ? 'Discharging...' : 'Discharge Patient'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Discharge;
